import { Box, Typography } from "@material-ui/core"
import { Dispatch, SetStateAction, useEffect, useState } from "react"
import styled from "styled-components"
import Calendar from "../Calendar"
import HeadingText from "../Texts/HeadingText"
const StyledBox = styled(Box)`
  margin-top: 30px;
  position: relative;
  > div {
    margin-top: 20px;
  }
  ${(p) => p.theme.breakpoints.up("md")} {
    margin-top: 20px;
  }
`

const StyledError = styled(Typography).attrs({ variant: "body1" })`
  color: #d70202;
  font-size: 18px;
  line-height: 1;
  text-align: right;
  position: absolute;
  bottom: -20px;
`

type GiftCardEndTimeProps = {
  setScheduleEndTime: Dispatch<SetStateAction<Date | string>>
  scheduleEndTime: Date | string
  frequency: string
  scheduleStartTime: Date | string
  scheduleError: string
}

const GiftCardEndTime = ({
  scheduleEndTime,
  setScheduleEndTime,
  frequency,
  scheduleStartTime,
  scheduleError,
}: GiftCardEndTimeProps) => {
  const [minDate, setMinDate] = useState(null)

  useEffect(() => {
    setAvailableDates()
  }, [frequency, scheduleStartTime])

  const getWeekDays = (date) => {
    const minSelectableDate = new Date(scheduleStartTime)
    const selectedDay = minSelectableDate.getDay() // Set to one week (7 days) after the start date
    const dayOfWeek = date.getDay()
    return (
      dayOfWeek === selectedDay && date.getTime() > minSelectableDate.getTime()
    )
  }

  const setAvailableDates = () => {
    const minSelectableDate = new Date(scheduleStartTime)
    if (frequency === "Daily") {
      setMinDate(minSelectableDate.setDate(minSelectableDate.getDate() + 1))
    }
    if (frequency === "Weekly") {
      const oneWeekLater = minSelectableDate
      oneWeekLater.setDate(oneWeekLater.getDate() + 7)
      setMinDate(oneWeekLater)
    }
    if (frequency === "Monthly") {
      const oneMonthLater = minSelectableDate
      oneMonthLater.setMonth(oneMonthLater.getMonth() + 1)
      if (oneMonthLater.getDate() !== new Date(scheduleStartTime).getDate()) {
        oneMonthLater.setMonth(new Date(scheduleStartTime).getMonth() + 1)
      }
      setMinDate(oneMonthLater)
    }
    if (frequency === "Yearly") {
      const oneYearLater = minSelectableDate
      oneYearLater.setFullYear(oneYearLater.getFullYear() + 1)
      if (oneYearLater.getMonth() !== new Date(scheduleStartTime).getMonth()) {
        oneYearLater.setMonth(minSelectableDate.getMonth() - 1)
      }
      setMinDate(oneYearLater)
    }
  }

  const isYearInEnabledRange = (date) => {
    const startDate = new Date(scheduleStartTime)
    const nextYearStartDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      startDate.getDate()
    )

    if (nextYearStartDate.getMonth() === date.getMonth()) {
      // The start date exists in the next year, select the same date
      return (
        startDate.getDate() === date.getDate() &&
        date.getMonth() === startDate.getMonth() &&
        startDate.getFullYear() !== date.getFullYear()
      )
    } else {
      // The start date doesn't exist in the next year, select the last day of the same month in the next year
      const lastDayOfNextYear = new Date(
        date.getFullYear(),
        date.getMonth() + 1,
        0
      )
      return (
        date.getDate() === lastDayOfNextYear.getDate() &&
        date.getMonth() === startDate.getMonth() &&
        startDate.getFullYear() !== date.getFullYear()
      )
    }
  }

  const getMonthDates = (date) => {
    const startDate = new Date(scheduleStartTime)
    const nextMonthStartDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      startDate.getDate()
    )

    if (nextMonthStartDate.getMonth() === date.getMonth()) {
      // The start date exists in the next month, select the same date
      return (
        startDate.getDate() === date.getDate() &&
        date.getTime() > startDate.getTime()
      )
    } else {
      // The start date doesn't exist in the next month, select the last day of the next month
      const lastDayOfNextMonth = new Date(
        date.getFullYear(),
        date.getMonth() + 1,
        0
      )
      return (
        date.getDate() === lastDayOfNextMonth.getDate() &&
        date.getTime() > startDate.getTime()
      )
    }
  }

  return (
    <StyledBox>
      <HeadingText text="הגיפטקארד האחרון ישלח בתאריך:" />
      <Calendar
        date={scheduleEndTime}
        setDate={setScheduleEndTime}
        minDate={minDate}
        filterDate={
          frequency === "Weekly"
            ? getWeekDays
            : frequency === "Yearly"
            ? isYearInEnabledRange
            : frequency === "Monthly"
            ? getMonthDates
            : null
        }
        type="endTime"
        disabled={!scheduleStartTime || !frequency}
      />
      {scheduleError && !scheduleEndTime && (
        <StyledError>{scheduleError}</StyledError>
      )}
    </StyledBox>
  )
}

export default GiftCardEndTime
